import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import EnvelopeLoader from "@/components/EnvelopeLoader.jsx";
import EarthTransition from "@/components/EarthTransition.jsx";
import VarmalaScroll from "@/components/VarmalaScroll.jsx";
import VarmalaVideo from "@/components/VarmalaVideo.jsx";
import Alliance from "@/components/Alliance.jsx";
import Families from "@/components/Families.jsx";
import Countdown from "@/components/Countdown.jsx";
import Nav from "@/components/Nav.jsx";
import OurStory from "@/components/OurStory.jsx";
import Itinerary from "@/components/Itinerary.jsx";
import Venue from "@/components/Venue.jsx";
import OrnamentDivider from "@/components/OrnamentDivider.jsx";
import RSVP from "@/components/RSVP.jsx";
import FooterBlock from "@/components/Footer.jsx";
import FilmGrain from "@/components/FilmGrain.jsx";
import AudioToggle from "@/components/AudioToggle.jsx";

import { useLenis } from "@/hooks/useLenis.js";
import { ScrollTrigger } from "@/lib/gsap";

// Default is the autoplaying video; these params keep the old scrubbed frames reachable.
const USE_FRAMES = ["classic", "clip", "frames"].includes(
  new URLSearchParams(window.location.search).get("varmala")
);

/**
 * Top-level state machine for the site.
 *
 *   envelope → earth → scroll
 *
 * We render all three scenes. `EnvelopeLoader` and `EarthTransition` are
 * fixed-position overlays that animate themselves in/out; the main scroll
 * content lives beneath them and is hidden (opacity 0 + body locked)
 * until the state reaches "scroll".
 */
export default function App() {
  const [phase, setPhase] = useState("envelope"); // "envelope" | "earth" | "scroll"

  // Smooth scroll — only enabled once the user has reached the scroll phase.
  useLenis(phase === "scroll");

  // Lock body scroll until we're in the scroll phase.
  useEffect(() => {
    if (phase === "scroll") {
      document.body.classList.remove("locked");
      // Open on the varmala video filling the screen; the header sits just
      // above it for anyone who scrolls up.
      requestAnimationFrame(() => {
        const video = document.getElementById("varmala");
        if (video) window.scrollTo({ top: video.offsetTop, behavior: "instant" });
      });
      // Refresh ScrollTrigger so its start/end calculations account for content
      // that only just became visible.
      ScrollTrigger.refresh();
    } else {
      document.body.classList.add("locked");
    }
  }, [phase]);

  const handleEnvelopeOpen = useCallback(() => {
    setPhase("earth");
  }, []);

  const handleEarthDone = useCallback(() => {
    setPhase("scroll");
  }, []);

  return (
    <>
      <FilmGrain />

      <AnimatePresence mode="wait">
        {phase === "envelope" && (
          <motion.div
            key="envelope"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="fixed inset-0 z-[500]"
          >
            <EnvelopeLoader onOpen={handleEnvelopeOpen} />
          </motion.div>
        )}

        {phase === "earth" && (
          <motion.div
            key="earth"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[400]"
          >
            <EarthTransition onComplete={handleEarthDone} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main site — hidden until we reach the scroll phase */}
      <motion.main
        id="main-content"
        initial={false}
        animate={{ opacity: phase === "scroll" ? 1 : 0 }}
        transition={{ duration: 0.5, delay: phase === "scroll" ? 0.1 : 0 }}
        className="relative"
      >
        <Nav />
        {USE_FRAMES ? <VarmalaScroll /> : <VarmalaVideo />}
        <Alliance />
        <OrnamentDivider />
        <OurStory />
        <OrnamentDivider />
        <Itinerary />
        <OrnamentDivider />
        <Venue />
        <OrnamentDivider />
        <RSVP />
        <Families />
        <Countdown />
        <FooterBlock />
      </motion.main>

      {/* Music toggle — visible from the very first screen */}
      <AudioToggle />
    </>
  );
}
